import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, AutoComplete, Space, message, Spin, Tooltip, Tag, Switch, Popconfirm
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    InfoCircleOutlined, ExpandOutlined,
    SearchOutlined, FileTextOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import adminService from '../../services/adminService';

const { TextArea } = Input;

/**
 * Icerik hucresi. Arama terimi icerikte geciyorsa gectigi yerin cevresini
 * gosterir ve terimi kalinlastirir — 113 kayit arasinda "bunu nereye
 * yazmisiz" sorusunun cevabi bu parcacik. Terim icerikte yoksa (or. yalnizca
 * baslikta eslesti) normal onizleme donulur.
 *
 * Icerik zaten listeye iniyor (KnowledgeBaseDTO content alanini tasiyor),
 * bu yuzden parcacik icin ek istek gerekmiyor.
 */
const renderContentCell = (content, term) => {
    if (!content) return '';
    const preview = () => content.substring(0, 80) + (content.length > 80 ? '...' : '');

    const t = (term || '').trim();
    if (!t) return preview();

    const idx = content.toLocaleLowerCase('tr').indexOf(t.toLocaleLowerCase('tr'));
    if (idx === -1) return preview();

    const start = Math.max(0, idx - 40);
    const end = Math.min(content.length, idx + t.length + 40);
    return (
        <>
            {start > 0 ? '...' : ''}
            {content.substring(start, idx)}
            <mark style={{
                background: 'var(--admin-accent-primary-light)',
                color: 'var(--admin-accent-primary)',
                fontWeight: 600,
                padding: '0 2px',
                borderRadius: 3,
            }}>
                {content.substring(idx, idx + t.length)}
            </mark>
            {content.substring(idx + t.length, end)}
            {end < content.length ? '...' : ''}
        </>
    );
};

const KnowledgeBasePage = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState({ title: '', content: '' });
    const [editingEntry, setEditingEntry] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [titleFilter, setTitleFilter] = useState('');
    const [debouncedTitle, setDebouncedTitle] = useState('');
    const [categories, setCategories] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [form] = Form.useForm();
    const debounceRef = useRef(null);

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const data = await adminService.getAllKnowledgeBase(categoryFilter, debouncedTitle || null, currentPage, pageSize);
            setEntries(data.content || []);
            setTotalElements(data.totalElements);
        } catch (err) {
            message.error('Bilgi kazanı kayıtları yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleTitleSearch = useCallback((value) => {
        setTitleFilter(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedTitle(value);
            setCurrentPage(0);
        }, 500);
    }, []);

    const fetchCategories = async () => {
        try {
            const data = await adminService.getKBCategories();
            setCategories(data || []);
        } catch (err) {
            // Kategori listesi yuklenemezse sessizce devam et
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [categoryFilter, debouncedTitle, currentPage, pageSize]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleAdd = () => {
        setEditingEntry(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingEntry(record);
        form.setFieldsValue({
            title: record.title,
            content: record.content,
            category: record.category,
            isActive: record.isActive,
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const isUpdate = !!editingEntry;
            if (isUpdate) {
                await adminService.updateKnowledgeBase(editingEntry.id, values);
                message.success('Kayıt güncellendi');
            } else {
                await adminService.createKnowledgeBase(values);
                message.success('Kayıt oluşturuldu');
            }
            setModalVisible(false);
            fetchCategories();
            fetchEntries();
        } catch (err) {
            if (err.errorFields) return;
            message.error('İşlem hatası');
        }
    };

    const handleDelete = async (id) => {
        try {
            await adminService.deleteKnowledgeBase(id);
            message.success('Kayıt silindi');
            // Sayfadaki son eleman silindiyse bir onceki sayfaya don
            if (entries.length === 1 && currentPage > 0) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchEntries();
            }
            fetchCategories();
        } catch (err) {
            message.error('Silme hatası');
        }
    };

    const handlePreview = (record) => {
        setPreviewContent({
            title: record.title,
            content: record.content,
            category: record.category,
        });
        setPreviewModalVisible(true);
    };

    const columns = [
        {
            title: 'Başlık',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (title) => (
                <span style={{ fontWeight: 500, color: 'var(--admin-text-primary)' }}>
                    {title}
                </span>
            ),
        },
        {
            title: 'Kategori',
            dataIndex: 'category',
            key: 'category',
            width: 130,
            render: (category) => (
                category ? (
                    <Tag color="blue" style={{ fontSize: 12 }}>{category}</Tag>
                ) : (
                    <span style={{ color: 'var(--admin-text-tertiary)' }}>-</span>
                )
            ),
        },
        {
            title: 'İçerik',
            dataIndex: 'content',
            key: 'content',
            render: (content, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        color: 'var(--admin-text-secondary)',
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        fontSize: 13,
                    }}>
                        {renderContentCell(content, debouncedTitle)}
                    </span>
                    <Tooltip title="Tam içeriği görüntüle">
                        <Button
                            type="text"
                            size="small"
                            icon={<ExpandOutlined />}
                            onClick={() => handlePreview(record)}
                            style={{ color: 'var(--admin-accent-primary)' }}
                        />
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 90,
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'default'} style={{ fontSize: 12 }}>
                    {isActive ? 'Aktif' : 'Pasif'}
                </Tag>
            ),
        },
        {
            title: 'Son Güncelleme',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (date) => (
                date ? (
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12 }}>
                        {new Date(date).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </span>
                ) : '-'
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space size={8}>
                    <Tooltip title="Düzenle">
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                        </motion.div>
                    </Tooltip>
                    <Tooltip title="Sil">
                        <Popconfirm
                            title="Bu kaydı silmek istediğinize emin misiniz?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Sil"
                            cancelText="İptal"
                        >
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <Button
                                    className="admin-btn-icon"
                                    icon={<DeleteOutlined />}
                                    danger
                                />
                            </motion.div>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Bilgi Kazanı</h1>
                    <p className="admin-page-subtitle">
                        Chatbot'un bilgi kaynağını yönetin. Buradaki içerikler LLM prompt'una eklenir.
                    </p>
                </div>
                <Space size={12}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                            className="admin-btn-primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            Yeni Kayıt
                        </Button>
                    </motion.div>
                </Space>
            </div>

            {/* Info Banner */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                }}
            >
                <InfoCircleOutlined style={{ color: '#6366f1', marginTop: 2 }} />
                <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--admin-text-primary)' }}>Nasıl Çalışır:</strong><br />
                    Buradaki <strong>aktif</strong> kayıtlar chatbot'un her LLM çağrısında system prompt'a eklenir.
                    Chatbot, kullanıcı sorularına bu bilgileri kullanarak cevap verir.
                    Pasif kayıtlar prompt'a dahil edilmez.
                </div>
            </motion.div>

            {/* Category Filter */}
            {categories.length > 0 && (
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Filtre</span>
                    <button
                        onClick={() => { setCategoryFilter(null); setCurrentPage(0); }}
                        style={{
                            padding: '6px 14px',
                            borderRadius: 8,
                            border: `1px solid ${!categoryFilter ? 'rgba(99, 102, 241, 0.5)' : 'var(--admin-border-color)'}`,
                            background: !categoryFilter ? 'rgba(99, 102, 241, 0.15)' : 'var(--admin-glass-bg)',
                            color: !categoryFilter ? '#818cf8' : 'var(--admin-text-secondary)',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit',
                        }}
                    >
                        Tümü
                    </button>
                    {categories.map(cat => {
                        const isActive = categoryFilter === cat;
                        return (
                            <button
                                key={cat}
                                onClick={() => { setCategoryFilter(isActive ? null : cat); setCurrentPage(0); }}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 8,
                                    border: `1px solid ${isActive ? 'rgba(6, 182, 212, 0.5)' : 'var(--admin-border-color)'}`,
                                    background: isActive ? 'rgba(6, 182, 212, 0.15)' : 'var(--admin-glass-bg)',
                                    color: isActive ? '#22d3ee' : 'var(--admin-text-secondary)',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    fontFamily: 'inherit',
                                }}
                            >
                                {cat}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Title Search */}
            <div style={{ marginBottom: 16 }}>
                <Input
                    prefix={<SearchOutlined style={{ color: 'var(--admin-text-tertiary)' }} />}
                    placeholder="Başlık veya içerikte ara..."
                    value={titleFilter}
                    onChange={(e) => handleTitleSearch(e.target.value)}
                    allowClear
                    onClear={() => { setTitleFilter(''); setDebouncedTitle(''); setCurrentPage(0); }}
                    style={{
                        maxWidth: 360,
                        background: 'var(--admin-glass-bg)',
                        borderColor: 'var(--admin-border-color)',
                    }}
                />
            </div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={entries}
                        rowKey="id"
                        pagination={{
                            current: currentPage + 1,
                            pageSize: pageSize,
                            total: totalElements,
                            onChange: (page, size) => {
                                setCurrentPage(page - 1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            showTotal: (total) => `Toplam ${total} kayıt`,
                        }}
                    />
                )}
            </motion.div>

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <Modal
                        title={
                            <div className="admin-modal-heading">
                                <div className="admin-modal-heading-icon">
                                    {editingEntry ? <EditOutlined /> : <PlusOutlined />}
                                </div>
                                <div>
                                    <div className="admin-modal-heading-title">
                                        {editingEntry ? 'Kayıt Düzenle' : 'Yeni Kayıt'}
                                    </div>
                                    <div className="admin-modal-heading-subtitle">
                                        {editingEntry
                                            ? "Chatbot'un cevaplarında kullanacağı bilgiyi düzenleyin"
                                            : "Chatbot'un bilgi kaynağına yeni bir kayıt ekleyin"}
                                    </div>
                                </div>
                            </div>
                        }
                        open={modalVisible}
                        onOk={handleSubmit}
                        onCancel={() => setModalVisible(false)}
                        okText={editingEntry ? 'Güncelle' : 'Oluştur'}
                        cancelText="İptal"
                        width="92vw"
                        style={{ maxWidth: 700 }}
                        className="admin-modal"
                        rootClassName="admin-modal"
                        okButtonProps={{ className: 'admin-btn-primary' }}
                        cancelButtonProps={{ className: 'admin-btn-secondary' }}
                    >
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="title"
                                label="Başlık"
                                rules={[{ required: true, message: 'Başlık gerekli' }]}
                            >
                                <Input placeholder="Örnek: ParaFly Gold Card" />
                            </Form.Item>

                            <Form.Item
                                name="category"
                                label="Kategori"
                                rules={[{ required: true, message: 'Kategori gerekli' }]}
                            >
                                <AutoComplete
                                    placeholder="Kategori seçin veya yeni yazın"
                                    allowClear
                                    options={categories.map(c => ({ label: c, value: c }))}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>

                            <Form.Item
                                name="content"
                                label="İçerik"
                                rules={[{ required: true, message: 'İçerik gerekli' }]}
                                extra="Bu içerik chatbot'un LLM prompt'una eklenir. Açık ve kısa yazın."
                            >
                                <TextArea
                                    rows={6}
                                    placeholder="Örnek: ParaFly Gold Card sahipleri %10 mil kazanımı, lounge erişimi..."
                                    style={{ fontSize: 13, lineHeight: 1.5 }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="isActive"
                                label="Durum"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren="Aktif"
                                    unCheckedChildren="Pasif"
                                />
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Preview Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <FileTextOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">
                                {previewContent.title}
                                {previewContent.category && (
                                    <Tag color="blue" style={{ fontSize: 11, marginLeft: 8 }}>
                                        {previewContent.category}
                                    </Tag>
                                )}
                            </div>
                            <div className="admin-modal-heading-subtitle">
                                Kaydın tam içeriğini görüntüleyin
                            </div>
                        </div>
                    </div>
                }
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={null}
                width="92vw"
                style={{ maxWidth: 700 }}
                className="admin-modal"
                rootClassName="admin-modal"
            >
                <div style={{
                    background: 'var(--admin-bg-tertiary)',
                    borderRadius: 'var(--admin-radius-md)',
                    padding: 20,
                    maxHeight: 400,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'var(--admin-text-secondary)',
                }}>
                    {previewContent.content}
                </div>
            </Modal>
        </div>
    );
};

export default KnowledgeBasePage;
